import Phaser from 'phaser';
import { TextureKey } from '../assets/manifest.js';
import { TUNING } from '../config/tuning.js';
import type { WorldScale } from '../core/WorldScale.js';

const C = TUNING.clouds;

interface Cloud {
  img: Phaser.GameObjects.Image;
  /** share of the scroll speed: small clouds are far away and slow, larger ones nearer and a bit faster (parallax) */
  parallax: number;
}

/**
 * Small clouds drifting by in the background, one after another over the game. Behind everything else
 * (also without own scenery, e.g. in front of a printed mural).
 */
export class Clouds {
  private readonly clouds: Cloud[] = [];
  /** scrolled distance (pixels) until the next cloud comes in from the right */
  private untilNext = 0;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly ws: WorldScale,
    private readonly viewWidth: number,
    private readonly depth: number,
  ) {
    // a few clouds are in view right from the start
    for (let i = 0; i < C.startCount; i++) this.spawn(Phaser.Math.FloatBetween(0.1, 0.9) * viewWidth);
    this.untilNext = this.gap();
  }

  /** Scroll by `dx` pixels (the world's scroll). */
  scroll(dx: number): void {
    for (const c of this.clouds) {
      if (!c.img.visible) continue;
      c.img.x -= dx * c.parallax;
      if (c.img.x + c.img.displayWidth < 0) c.img.setVisible(false);
    }
    this.untilNext -= dx;
    if (this.untilNext <= 0) {
      this.spawn(this.viewWidth);
      this.untilNext = this.gap();
    }
  }

  /** distance (pixels of the world's scroll) to the next cloud */
  private gap(): number {
    return this.ws.px(Phaser.Math.FloatBetween(C.gapMinSec, C.gapMaxSec) * TUNING.world.scrollSpeed);
  }

  private spawn(x: number): void {
    // 0 = smallest (far), 1 = largest (near)
    const near = Math.random();
    const width = this.ws.px(Phaser.Math.Linear(C.minWidth, C.maxWidth, near));
    let c = this.clouds.find((o) => !o.img.visible);
    if (!c) {
      c = { img: this.scene.add.image(0, 0, TextureKey.Cloud).setOrigin(0, 0.5).setDepth(this.depth), parallax: 0 };
      this.clouds.push(c);
    }
    c.parallax = Phaser.Math.Linear(C.minParallax, C.maxParallax, near);
    const y = this.ws.pixelHeight * Phaser.Math.FloatBetween(C.minYFraction, C.maxYFraction);
    c.img
      .setDisplaySize(width, (width * C.aspect[1]) / C.aspect[0])
      .setPosition(x, y)
      .setAlpha(Phaser.Math.Linear(C.minAlpha, 1, near))
      .setVisible(true);
  }
}
